import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";

/** Keyword → service slug map used to point blog readers at the money pages. */
const MATCHES: { re: RegExp; slugs: string[] }[] = [
  { re: /moss|lichen/i, slugs: ["roof-moss-removal", "roof-washing"] },
  { re: /roof|shingle|algae|black streak/i, slugs: ["roof-washing", "roof-moss-removal"] },
  { re: /gutter guard|leaf guard/i, slugs: ["gutter-guards", "gutter-cleaning"] },
  { re: /gutter/i, slugs: ["gutter-cleaning", "gutter-guards"] },
  { re: /siding|house wash|vinyl|exterior/i, slugs: ["house-washing", "soft-washing"] },
  { re: /soft wash/i, slugs: ["soft-washing", "house-washing"] },
  { re: /deck|patio/i, slugs: ["deck-patio-cleaning", "fence-cleaning"] },
  { re: /paver|hardscape/i, slugs: ["paver-cleaning-sealing", "concrete-cleaning-sealing"] },
  { re: /concrete|driveway|sidewalk|walkway|sealing/i, slugs: ["concrete-cleaning-sealing", "driveway-cleaning"] },
  { re: /fence/i, slugs: ["fence-cleaning", "deck-patio-cleaning"] },
  { re: /window/i, slugs: ["window-cleaning", "house-washing"] },
  { re: /commercial|business|storefront/i, slugs: ["commercial-pressure-washing"] },
  { re: /christmas|holiday light/i, slugs: ["holiday-lighting"] },
  { re: /rust|stain/i, slugs: ["rust-stain-removal", "concrete-cleaning-sealing"] },
  { re: /pressure wash|power wash|clean/i, slugs: ["house-washing", "concrete-cleaning-sealing"] },
];

function pickSlugs(text: string): string[] {
  const out: string[] = [];
  for (const m of MATCHES) {
    if (!m.re.test(text)) continue;
    for (const s of m.slugs) if (!out.includes(s)) out.push(s);
    if (out.length >= 3) break;
  }
  return out.slice(0, 3);
}

/**
 * Contextual internal links from a blog article to the service pages that can
 * actually book the job. Only rendered on English articles.
 */
export function BlogServiceLinks({ title, slug }: { title: string; slug: string }) {
  const picked = pickSlugs(`${title} ${slug.replace(/-/g, " ")}`)
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter((s): s is (typeof SERVICES)[number] => Boolean(s));

  if (picked.length === 0) return null;

  return (
    <section className="mt-12 rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6">
      <h2
        className="text-[var(--brand-deep-blue)] text-lg md:text-xl uppercase mb-4"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
      >
        Services Related To This Article
      </h2>
      <ul className="grid gap-3 sm:grid-cols-3">
        {picked.map((s) => (
          <li key={s.slug}>
            <Link
              to="/$service"
              params={{ service: s.slug }}
              className="flex h-full items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 transition hover:border-[var(--brand-red)] hover:shadow-md"
            >
              <span className="text-sm font-semibold leading-snug text-[var(--brand-deep-blue)]">
                {s.name} in Albany &amp; the Capital Region
              </span>
              <ArrowRight
                size={16}
                className="mt-0.5 shrink-0 text-[var(--brand-red)]"
                aria-hidden="true"
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
